import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wind, ChevronLeft, Eye, EyeOff, ArrowLeft, Mail, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();

  // Parse query params: ?mode=register|login|forgot and ?next=/some/path
  const searchParams = new URLSearchParams(location.search);
  const initialMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const nextPath = searchParams.get('next') || '/breathing';

  // After login, land on the requested next path (or the exercises hub by default).
  const from = nextPath;

  // Mode: 'login', 'register', 'forgot', 'reset'
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success("Connexion réussie !");
        navigate(from, { replace: true });
      } else if (mode === 'register') {
        if (!name.trim()) {
          toast.error("Veuillez entrer votre nom");
          setLoading(false);
          return;
        }
        await register(email, password, name);
        // Auto-approved flow: register now logs the user in immediately.
        toast.success('Compte créé avec succès !');
        navigate(from, { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Veuillez entrer votre email");
      return;
    }
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Un lien de réinitialisation a été envoyé à votre email. Vérifiez votre boîte de réception.");
        setEmailSent(true);
      } else {
        toast.error(data.detail || "Une erreur est survenue");
      }
    } catch (error) {
      toast.error("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetToken.trim()) {
      toast.error("Veuillez entrer le code reçu par email");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success("Mot de passe modifié avec succès !");
        setMode('login');
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setResetToken('');
        setEmailSent(false);
      } else {
        toast.error(data.detail || "Code invalide ou expiré");
      }
    } catch (error) {
      toast.error("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'register': return "Créer un compte";
      case 'forgot': return "Mot de passe oublié";
      case 'reset': return "Nouveau mot de passe";
      default: return "Respiration Santé";
    }
  };

  const getDescription = () => {
    switch (mode) {
      case 'register': return "Créez votre compte pour commencer";
      case 'forgot': return "Entrez votre email pour recevoir un lien de réinitialisation.";
      case 'reset': return "Entrez le code reçu et votre nouveau mot de passe";
      default: return "Connectez-vous pour accéder à vos statistiques";
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-6 relative" 
      data-testid="login-page"
      style={{ backgroundColor: '#1a1a2e' }}
    >
      {/* Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-[1]"
      >
        <source src="/videos/app-background.mp4" type="video/mp4" />
      </video>
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/20 z-[2]" />
      
      {/* Content */}
      <Card className="w-full max-w-sm bg-white/10 backdrop-blur-md border-white/20 shadow-xl relative z-[3]">
        <CardHeader className="text-center">
          {mode === 'forgot' || mode === 'reset' ? (
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <KeyRound className="w-8 h-8 text-cyan-400" />
            </div>
          ) : (
            <div className="mx-auto mb-4 flex justify-center">
              <img
                src="/logo-mains-nuages-mobile.png"
                alt="Physiothérapie Les Mains Nuages"
                className="h-24 w-auto rounded-2xl"
                style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}
                data-testid="login-brand-logo"
              />
            </div>
          )}
          <CardTitle className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {getTitle()}
          </CardTitle>
          <CardDescription className="text-white/60">
            {getDescription()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Login / Register Form */}
          {(mode === 'login' || mode === 'register') && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div>
                  <Label htmlFor="name" className="text-white/80">Nom</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Votre nom"
                    className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    data-testid="name-input"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="email" className="text-white/80">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-white/80">Mot de passe</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="pr-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-500 hover:bg-cyan-600"
                data-testid="submit-btn"
              >
                {loading ? "Chargement..." : (mode === 'login' ? "Se connecter" : "Créer un compte")}
              </Button>
            </form>
          )}

          {/* Forgot Password — sends a reset link by email */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              {emailSent ? (
                <div className="text-center space-y-3 py-4">
                  <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <KeyRound className="w-6 h-6 text-emerald-400" />
                  </div>
                  <p className="text-white/90 text-sm leading-relaxed">
                    Un lien de réinitialisation a été envoyé à<br />
                    <span className="font-semibold text-cyan-300">{email}</span>
                  </p>
                  <p className="text-white/60 text-xs">
                    Le lien expire dans 1 heure. Pensez à vérifier vos spams si rien n&apos;arrive dans 2 minutes.
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <Label htmlFor="forgot-email" className="text-white/80">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      required
                      className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-cyan-500 hover:bg-cyan-600"
                    data-testid="send-reset-btn"
                  >
                    {loading ? "Envoi..." : "Envoyer le lien"}
                  </Button>
                </>
              )}
            </form>
          )}

          {/* Reset Password Form */}
          {mode === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {emailSent && (
                <div className="p-3 rounded-lg bg-cyan-500/20 border border-cyan-500/30 mb-4">
                  <p className="text-cyan-300 text-sm text-center">
                    <Mail className="inline w-4 h-4 mr-1" />
                    Code envoyé à {email}
                  </p>
                </div>
              )}
              <div>
                <Label htmlFor="reset-token" className="text-white/80">Code de réinitialisation</Label>
                <Input
                  id="reset-token"
                  type="text"
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value)}
                  placeholder="Collez le code reçu par email"
                  required
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="reset-token-input"
                />
              </div>
              <div>
                <Label htmlFor="new-password" className="text-white/80">Nouveau mot de passe</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password" className="text-white/80">Confirmer le mot de passe</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-500 hover:bg-cyan-600"
                data-testid="reset-password-btn"
              >
                {loading ? "Modification..." : "Modifier le mot de passe"}
              </Button>
            </form>
          )}
          
          {/* Navigation Links */}
          <div className="mt-4 text-center space-y-3">
            {mode === 'login' && (
              <>
                <button
                  onClick={() => setMode('forgot')}
                  className="text-sm text-white/50 hover:text-cyan-400"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </button>
                <div className="pt-3 border-t border-white/10">
                  <p className="text-sm text-white/70 mb-2">Nouveau sur la plateforme ?</p>
                  <button
                    onClick={() => setMode('register')}
                    className="inline-flex items-center justify-center w-full px-4 py-2.5 rounded-full border border-cyan-400/60 text-cyan-300 hover:bg-cyan-400/10 hover:text-cyan-200 text-sm font-semibold transition-colors"
                    data-testid="toggle-auth-mode"
                  >
                    Créer un compte
                  </button>
                </div>
              </>
            )}
            
            {mode === 'register' && (
              <button
                onClick={() => setMode('login')}
                className="block w-full text-sm text-cyan-400 hover:text-cyan-300"
                data-testid="toggle-auth-mode"
              >
                Déjà un compte ? Connectez-vous
              </button>
            )}
            
            {(mode === 'forgot' || mode === 'reset') && (
              <button
                onClick={() => {
                  setMode('login');
                  setEmailSent(false);
                  setResetToken('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="inline-flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300"
                data-testid="back-to-login"
              >
                <ArrowLeft size={16} />
                Retour à la connexion
              </button>
            )}
            
            <div className="pt-2 border-t border-white/10">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-1 text-sm text-white/50 hover:text-cyan-400 transition-colors"
                data-testid="back-to-home"
              >
                <ChevronLeft size={16} />
                Retour à l&apos;accueil
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
